import { useEffect } from 'react';
import { fetchTrialStatus, type TrialStatus } from '../api/trial.api';
import { TRIAL_STATUS_STALE_EVENT } from '../api/client';
import { useAppStore } from '../store/appStore';
import { useCurrentRole } from './usePermissions';

/** A cached status younger than this is reused when another screen mounts. */
const STALE_AFTER_MS = 60_000;
/**
 * Widget visitors use up the allowance too, which the dashboard never sees
 * happen — so the status is polled while the tab is visible, not only
 * refreshed after the user's own test chats.
 */
const POLL_EVERY_MS = 60_000;

/**
 * Shared across every mounted hook: each screen renders its own sidebar
 * layout, so without this a route change would fire a second request while
 * the first is still out.
 */
let inFlight: Promise<void> | null = null;
/** A forced refresh asked for while a request was already out. */
let refetchQueued = false;

function load(force: boolean) {
  const { trialStatusFetchedAt } = useAppStore.getState();
  if (!force && Date.now() - trialStatusFetchedAt < STALE_AFTER_MS) return;
  if (inFlight) {
    // The request already out may have been answered before the change that
    // made the status stale, so go again once it lands.
    if (force) refetchQueued = true;
    return;
  }

  inFlight = fetchTrialStatus()
    .then((status) => {
      // Signed out (or switched user) while the request was out.
      if (useAppStore.getState().user?.role !== 'CUSTOMER') return;
      useAppStore.getState().setTrialStatus(status);
    })
    .catch(() => {
      // Advisory only — keep the last known status rather than flash the
      // banner away; the backend still enforces the limit on every chat.
    })
    .finally(() => {
      inFlight = null;
      if (refetchQueued) {
        refetchQueued = false;
        load(true);
      }
    });
}

/**
 * The organization's free-trial status (GET /onboarding/trial), for CUSTOMER
 * only — the demo sign-up role is the one that runs on a trial. Every other
 * role gets null and no request is made.
 *
 * Gated on the role rather than the permission matrix on purpose: `can()` lets
 * SUPER_ADMIN through, and a platform user has no organization to ask about.
 *
 * Refreshes on mount when stale, on a timer while the tab is visible, when the
 * tab comes back into view, and immediately on TRIAL_STATUS_STALE_EVENT (a
 * test message sent, or a chat refused by the trial guard).
 */
export function useTrialStatus(): TrialStatus | null {
  const isCustomer = useCurrentRole() === 'CUSTOMER';
  const status = useAppStore((s) => s.trialStatus);

  useEffect(() => {
    if (!isCustomer) return;

    load(false);

    const onStale = () => load(true);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') load(false);
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, POLL_EVERY_MS);

    window.addEventListener(TRIAL_STATUS_STALE_EVENT, onStale);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(TRIAL_STATUS_STALE_EVENT, onStale);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isCustomer]);

  return isCustomer ? status : null;
}
